import CounterpartValidator from "Validators/Admin/Counterpart";
import { useEffect, useState } from "react";

import { HTTPErrorCallback } from "Helpers/Error";
import { docExtensions, imageExtensions } from "Helpers/File";

import UseFile from "Hooks/UseFile";
import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";
import { useFormSchema } from "Hooks/useFormSchema";

import Button from "Components/Admin/Buttons";
import FileUploader from "Components/Admin/FileUploader";
import FormWrapper from "Components/Admin/Form/FormWrapper";
import Input from "Components/Admin/Form/Input";
import Select from "Components/Admin/Form/Select";
import GetIcon from "Components/Global/Icons";

import * as S from "./styles";

const CounterpartForm = ({
  projectId,
  selectedCounterpart,
  setSelectedCounterpart,
  linkOptions,
  statusOptions,
  getProjectCounterparts,
}) => {
  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const { loading, setLoading } = UseGlobalLoading();
  const { uploadFile } = UseFile();

  const [files, setFiles] = useState([]);

  const [selectedLink, setSelectedLink] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  useEffect(() => {
    if (!selectedCounterpart?.id) return;

    // Contrapartida LEGADA presa a vínculo direto não está nas opções: o campo
    // fica vazio e o submit OMITE a chave, mantendo o vínculo (RN2).
    setSelectedLink(
      linkOptions?.find(
        (option) => option.value === selectedCounterpart.project_sponsor_id,
      ) || null,
    );

    setSelectedStatus(
      statusOptions?.find(
        (status) => status.value === selectedCounterpart.status,
      ),
    );

    if (selectedCounterpart.files) setFiles(selectedCounterpart.files);
  }, [selectedCounterpart, linkOptions]);

  const methods = useFormSchema(CounterpartValidator, {
    defaultValues: {
      description: selectedCounterpart?.description,
    },
  });

  const { register, reset } = methods;

  const submit = async (data) => {
    setLoading(true);

    try {
      const files_to_send = [];

      for (const file of files) {
        const image_location = file.file_src
          ? file.file_src
          : await uploadFile(file?.file);

        files_to_send.push({
          id: file?.id,
          name: file.name,
          file_src: image_location,
        });
      }

      const route = selectedCounterpart?.id
        ? "/counterparts/update-counterpart"
        : "/counterparts/create-counterpart";

      await api.post(route, {
        ...data,
        counterpart_id: selectedCounterpart?.id,
        project_id: projectId,
        // Chave omitida = "manter o vínculo atual" (contrato de 3 estados).
        ...(selectedLink?.value
          ? { project_sponsor_id: selectedLink.value }
          : {}),
        status: selectedStatus?.value,
        files: files_to_send,
      });

      setLoading(false);

      showNotification(
        "success",
        "Sucesso!",
        "Contrapartida atualizada com sucesso!",
      );

      reset();
      setSelectedCounterpart(null);
      getProjectCounterparts(projectId);
    } catch (error) {
      setLoading(false);
      return HTTPErrorCallback(error);
    }
  };

  return (
    <S.Wrapper>
      <S.Modal>
        <FormWrapper {...methods} submit={submit}>
          <S.Section>
            <span>{selectedCounterpart?.id ? "Atualizar" : "Cadastrar"}</span>
            <hr />
          </S.Section>

          {/* Formulário enxuto (D10 da parte 2, 03/09/2026): Nome, Tipo,
              Quantidade e Cota saíram. As colunas continuam no banco pelo
              histórico — a aplicação apenas não as escreve mais. */}
          <Input
            {...register("description")}
            label="Descrição"
            placeholder="Descrição da contrapartida"
            required
            type="textarea"
            height="160px"
          />

          {/* Era "Patrocinador" e vinha sempre vazio — mesma causa e mesma
              correção do formulário de Aporte (D1/D8, parte 4). */}
          <Select
            name="project_sponsor_id"
            label="Seletiva"
            options={linkOptions}
            placeholder="Selecione uma Seletiva"
            value={selectedLink}
            onChange={setSelectedLink}
            maxWidth="100%"
            required
          />

          <Select
            name="status"
            label="Status"
            options={statusOptions}
            placeholder="Selecione um Status"
            value={selectedStatus}
            onChange={setSelectedStatus}
            maxWidth="100%"
            required
          />

          <FileUploader
            allowMultiple={false}
            label="Arquivos"
            loading={loading}
            allowedExtensions={[...docExtensions, ...imageExtensions]}
            setFiles={(file) =>
              setFiles((prev) => [
                ...prev,
                {
                  file: file[0],
                  name: file[0].name,
                },
              ])
            }
          />

          {files.map((file) => (
            <S.File key={file.name}>
              <S.FileInfos>
                {file.file_src ? (
                  <a href={file.file_src} rel="noreferrer" target="_blank">
                    {file.name}
                  </a>
                ) : (
                  <h2>{file.name}</h2>
                )}

                <GetIcon
                  name="X"
                  onClick={() =>
                    setFiles((prev) => prev.filter((f) => f.name !== file.name))
                  }
                />
              </S.FileInfos>
            </S.File>
          ))}

          <S.Buttons>
            <Button
              type="button"
              outline
              text="Fechar"
              disabled={loading}
              onClick={() => setSelectedCounterpart(null)}
            />

            <Button
              type="submit"
              text={selectedCounterpart?.id ? "Atualizar" : "Cadastrar"}
              disabled={loading}
            />
          </S.Buttons>
        </FormWrapper>
      </S.Modal>
    </S.Wrapper>
  );
};

export default CounterpartForm;
